import type { BookingStatus } from '@makhe/shared';
import { cn } from '@/lib/cn';

// Ordered progress stages. A booking's status maps to the FURTHEST stage it has
// reached; every earlier stage is then treated as reached too (cumulative), so
// the indicator never visually regresses (e.g. at `accepted` the "Requested"
// stage stays checked).
const STAGE_LABELS = ['Requested', 'Accepted', 'In progress', 'Confirmed'];

function stageIndex(status: BookingStatus): number {
  switch (status) {
    case 'requested':
    case 'counter_proposed':
      return 0;
    case 'accepted':
      return 1;
    case 'in_progress':
      return 2;
    case 'awaiting_confirmation':
      return 2;
    case 'completed':
      return 3;
    default:
      return -1; // terminal / not on the happy path
  }
}

// Benign terminal outcomes read as neutral, not alarming red. Only genuinely
// adverse states (dispute / no-show) get a warning tone.
const TERMINAL: Partial<Record<BookingStatus, { label: string; adverse: boolean }>> = {
  declined: { label: 'Declined', adverse: false },
  cancelled_by_buyer: { label: 'Cancelled by buyer', adverse: false },
  cancelled_by_seller: { label: 'Cancelled by seller', adverse: false },
  no_show_buyer: { label: 'No-show: buyer', adverse: true },
  no_show_seller: { label: 'No-show: seller', adverse: true },
  disputed: { label: 'Disputed', adverse: true },
};

export function BookingStatusStepper({ status }: { status: BookingStatus }) {
  const terminal = TERMINAL[status];
  if (terminal) {
    return (
      <div
        className={cn(
          'rounded-2xl p-4 text-sm',
          terminal.adverse
            ? 'bg-makhe-plum/15 text-makhe-plum'
            : 'bg-makhe-surface text-makhe-ink',
        )}
      >
        {terminal.label}
      </div>
    );
  }
  const current = stageIndex(status);
  return (
    <div className="flex items-center gap-2">
      {STAGE_LABELS.map((label, i) => {
        const reached = i <= current;
        return (
          <div key={label} className="flex flex-1 flex-col items-center">
            <div
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs',
                reached ? 'bg-makhe-primary' : 'bg-makhe-line/50',
              )}
            >
              {reached ? '✓' : i + 1}
            </div>
            <p className="mt-1 text-[10px] text-makhe-muted">{label}</p>
          </div>
        );
      })}
    </div>
  );
}
